"use client";

import { useRef, type ReactNode } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(useGSAP);

export function SectionTransition({
  sectionKey,
  children,
}: {
  sectionKey: string;
  children: ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstRun = useRef(true);

  useGSAP(
    () => {
      const el = containerRef.current;
      if (!el) return;

      const mm = gsap.matchMedia();
      mm.add(
        {
          reduceMotion: "(prefers-reduced-motion: reduce)",
          normal: "(prefers-reduced-motion: no-preference)",
        },
        (ctx) => {
          const reduce = ctx.conditions?.reduceMotion;
          gsap.fromTo(
            el,
            { autoAlpha: 0 },
            {
              autoAlpha: 1,
              duration: reduce ? 0 : isFirstRun.current ? 0.35 : 0.3,
              ease: "power2.out",
            }
          );
        }
      );

      isFirstRun.current = false;
    },
    { dependencies: [sectionKey], scope: containerRef, revertOnUpdate: true }
  );

  return (
    <div
      ref={containerRef}
      className="size-full overflow-y-auto overflow-x-hidden overscroll-contain"
    >
      {children}
    </div>
  );
}

export function PersistentLayer({
  active,
  children,
}: {
  active: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isFirstRun = useRef(true);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;

      if (isFirstRun.current) {
        gsap.set(el, { autoAlpha: active ? 1 : 0 });
        isFirstRun.current = false;
        return;
      }

      const mm = gsap.matchMedia();
      mm.add(
        {
          reduceMotion: "(prefers-reduced-motion: reduce)",
          normal: "(prefers-reduced-motion: no-preference)",
        },
        (ctx) => {
          const reduce = ctx.conditions?.reduceMotion;
          gsap.to(el, {
            autoAlpha: active ? 1 : 0,
            duration: reduce ? 0 : active ? 0.3 : 0.2,
            ease: active ? "power2.out" : "power2.in",
          });
        }
      );
    },
    { dependencies: [active], scope: ref }
  );

  return (
    <div
      ref={ref}
      className="absolute inset-0 size-full overflow-y-auto overflow-x-hidden lg:overflow-hidden overscroll-contain"
      style={{ pointerEvents: active ? "auto" : "none" }}
    >
      {children}
    </div>
  );
}
